import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, type Location } from 'react-router-dom';
import { Sidebar, CreateWorldWizard, SettingsModal, ImportWorldWizard, CreditAmountPicker, PurchaseCelebration } from '../components';
import { useAuthStore, useWorldsStore } from '../store';
import { useSidebarStore } from '../store/ui';
import {
  updateCurrentUser,
  getCreditsBalance,
  getBillingStatus,
  createBillingPortalSession,
  getPurchaseStatus,
  resendVerificationEmail,
  type BillingStatus,
  type CreditsBalance,
} from '../api';
import type { User } from '../types';

interface AccountContentProps {
  user: User;
  onClose?: () => void;
  variant?: 'page' | 'modal';
}

function formatMicros(micros: number): string {
  const dollars = micros / 1_000_000;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

function AccountContent({ user, onClose, variant = 'page' }: AccountContentProps) {
  const setUser = useAuthStore(state => state.setUser);
  const checkAuth = useAuthStore(state => state.checkAuth);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [credits, setCredits] = useState<CreditsBalance | null>(null);
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [loadingBilling, setLoadingBilling] = useState(true);
  const [verifyStatus, setVerifyStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [celebration, setCelebration] = useState<{
    pending: boolean;
    creditsMicros: number | null;
    balanceMicros: number | null;
  } | null>(null);

  const isVerified = !!user.is_admin || !!user.email_verified;

  const [form, setForm] = useState({
    display_name: user.display_name ?? '',
    handle: user.handle ?? '',
    avatar_url: user.avatar_url ?? '',
    bio: user.bio ?? '',
    timezone: user.timezone ?? '',
    email: user.email ?? '',
  });

  useEffect(() => {
    setForm({
      display_name: user.display_name ?? '',
      handle: user.handle ?? '',
      avatar_url: user.avatar_url ?? '',
      bio: user.bio ?? '',
      timezone: user.timezone ?? '',
      email: user.email ?? '',
    });
  }, [user]);

  useEffect(() => {
    let isMounted = true;
    setLoadingBilling(true);
    Promise.all([getCreditsBalance(), getBillingStatus()])
      .then(([creditsResult, billingResult]) => {
        if (!isMounted) return;
        setCredits(creditsResult);
        setBilling(billingResult);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err instanceof Error ? err.message : 'Failed to load billing data');
      })
      .finally(() => {
        if (!isMounted) return;
        setLoadingBilling(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Stripe sends the buyer back to /account?billing=success&session_id=...
  // Nothing used to read that, so a completed purchase produced no
  // acknowledgement at all. Poll until the webhook grants the credits, then
  // celebrate with the real amount rather than a hopeful guess.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') !== 'success') return;
    const sessionId = params.get('session_id');

    setCelebration({ pending: true, creditsMicros: null, balanceMicros: null });

    // Strip the billing params so a refresh does not replay the celebration.
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);

    if (!sessionId) {
      // Older checkout links carry no session id — confirm without an amount.
      setCelebration({ pending: false, creditsMicros: null, balanceMicros: null });
      return;
    }

    let cancelled = false;
    let attempts = 0;
    // ~20s of polling: comfortably longer than a normal Stripe webhook delay,
    // short enough that a stuck webhook does not spin forever.
    const maxAttempts = 20;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const result = await getPurchaseStatus(sessionId);
        if (cancelled) return;
        if (result.status === 'complete') {
          setCelebration({
            pending: false,
            creditsMicros: result.credits_micros,
            balanceMicros: result.balance_micros,
          });
          if (result.balance_micros !== null) {
            setCredits((prev) => ({
              balance_micros: result.balance_micros as number,
              last_reconciled_at: prev?.last_reconciled_at ?? null,
            }));
          }
          return;
        }
      } catch {
        // Transient failure — keep polling; the modal stays in its waiting state.
      }
      if (attempts < maxAttempts) {
        window.setTimeout(poll, 1000);
      } else if (!cancelled) {
        // Payment is real, fulfilment is just slow. Say so honestly.
        setCelebration({ pending: false, creditsMicros: null, balanceMicros: null });
      }
    };

    void poll();
    return () => {
      cancelled = true;
    };
  }, []);

  const displayInitial = useMemo(() => {
    return user.display_name?.[0] || user.email?.[0] || '?';
  }, [user.display_name, user.email]);

  const handleResendVerification = async () => {
    setVerifyStatus('sending');
    try {
      await resendVerificationEmail();
      setVerifyStatus('sent');
    } catch {
      setVerifyStatus('error');
    }
  };

  const handleSave = async () => {
    setError(null);
    setIsSaving(true);
    try {
      const payload: Parameters<typeof updateCurrentUser>[0] = {
        display_name: form.display_name || null,
        handle: form.handle || null,
        avatar_url: form.avatar_url || null,
        bio: form.bio || null,
        timezone: form.timezone || null,
      };
      // Include email only if changed
      if (form.email && form.email !== user.email) {
        payload.email = form.email;
      }
      const updated = await updateCurrentUser(payload);
      setUser(updated);
      await checkAuth();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleManageBilling = async () => {
    setError(null);
    try {
      const session = await createBillingPortalSession();
      if (session.url) {
        window.location.href = session.url;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
    }
  };

  return (
    <div className={variant === 'modal' ? 'w-full max-w-3xl' : 'w-full'}>
      {celebration && (
        <PurchaseCelebration
          pending={celebration.pending}
          creditsMicros={celebration.creditsMicros}
          balanceMicros={celebration.balanceMicros}
          onDismiss={() => setCelebration(null)}
        />
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-dark-50">Account Profile</h1>
          <p className="text-sm text-dark-300">Manage your public profile and billing settings.</p>
        </div>
        {onClose && (
          <button onClick={onClose} className="btn-secondary">Close</button>
        )}
      </div>

      {error && (
        <div className="mt-4 bg-red-900/40 border border-red-700 text-red-200 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-6">
          <div className="bg-dark-800 border border-dark-600 rounded-xl p-5">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-full bg-primary-600 text-white flex items-center justify-center text-xl font-semibold">
                {displayInitial}
              </div>
              <div>
                <div className="text-lg text-dark-50 font-medium">{user.display_name || user.email}</div>
                <div className="text-sm text-dark-400">{user.email}</div>
                <div className="mt-1 flex items-center gap-2">
                  {isVerified ? (
                    <span className="inline-flex items-center gap-1 text-xs text-green-400">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Verified
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-xs text-amber-400">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
                      </svg>
                      Not verified
                      {verifyStatus === 'sent' ? (
                        <span className="text-green-400 ml-1">Email sent!</span>
                      ) : (
                        <button
                          onClick={handleResendVerification}
                          disabled={verifyStatus === 'sending'}
                          className="underline hover:text-amber-200 disabled:opacity-50"
                        >
                          {verifyStatus === 'sending' ? 'Sending...' : 'Resend'}
                        </button>
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 grid gap-4">
              <div>
                <label className="block text-sm text-dark-300 mb-1">Email</label>
                <input
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="narrative-input w-full px-4 py-2"
                  placeholder="you@example.com"
                  type="email"
                />
                {form.email !== user.email && form.email && (
                  <p className="text-xs text-amber-400 mt-1">Changing your email will require re-verification.</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-dark-300 mb-1">Display name</label>
                <input
                  value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                  className="narrative-input w-full px-4 py-2"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-sm text-dark-300 mb-1">Handle</label>
                <input
                  value={form.handle}
                  onChange={(e) => setForm({ ...form, handle: e.target.value })}
                  className="narrative-input w-full px-4 py-2"
                  placeholder="handle"
                />
              </div>
              <div>
                <label className="block text-sm text-dark-300 mb-1">Avatar URL</label>
                <input
                  value={form.avatar_url}
                  onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
                  className="narrative-input w-full px-4 py-2"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm text-dark-300 mb-1">Timezone</label>
                <input
                  value={form.timezone}
                  onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  className="narrative-input w-full px-4 py-2"
                  placeholder="America/Los_Angeles"
                />
              </div>
              <div>
                <label className="block text-sm text-dark-300 mb-1">Bio</label>
                <textarea
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  className="narrative-input w-full px-4 py-2 min-h-[120px]"
                  placeholder="Tell us about yourself"
                />
              </div>
            </div>

            <div className="mt-5 flex items-center justify-end gap-3">
              <button onClick={handleSave} className="btn-primary" disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-dark-800 border border-dark-600 rounded-xl p-5">
            <h2 className="text-lg font-semibold text-dark-50">Credits</h2>
            {loadingBilling ? (
              <div className="mt-4 text-sm text-dark-300">Loading credits...</div>
            ) : (
              <>
                <div className="mt-4 text-3xl font-semibold text-primary-300">
                  {formatMicros(credits?.balance_micros ?? 0)}
                </div>
                <div className="text-xs text-dark-400 mt-1">
                  Last reconciled: {credits?.last_reconciled_at ?? 'Not yet'}
                </div>
                <div className="mt-4">
                  <CreditAmountPicker
                    isVerified={isVerified}
                    submitLabel="Add credits"
                    onError={setError}
                  />
                </div>
              </>
            )}
          </div>

          <div className="bg-dark-800 border border-dark-600 rounded-xl p-5">
            <h2 className="text-lg font-semibold text-dark-50">Billing History</h2>
            {loadingBilling ? (
              <div className="mt-4 text-sm text-dark-300">Loading billing status...</div>
            ) : (
              <>
                <div className="mt-2 text-sm text-dark-300">
                  {billing?.customer ? 'Stripe customer linked' : 'No billing history'}
                </div>
                {billing?.customer && (
                  <div className="mt-4">
                    <button onClick={handleManageBilling} className="btn-secondary w-full">
                      Manage Billing
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AccountPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();
  const { worlds, fetchWorlds } = useWorldsStore();
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [importHandoff, setImportHandoff] = useState<{ title: string; prompt: string } | null>(null);
  const sidebarOpen = useSidebarStore(state => state.sidebarOpen);
  const toggleSidebar = useSidebarStore(state => state.toggleSidebar);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWorlds();
    }
  }, [isAuthenticated, fetchWorlds]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-900">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="flex h-screen bg-dark-900">
      <Sidebar
        worlds={worlds}
        onCreateWorld={() => setShowCreateWizard(true)}
        onImportWorld={() => setShowImportWizard(true)}
        onOpenSettings={() => setShowSettings(true)}
      />

      <main className="flex-1 overflow-auto flex flex-col">
        {/* Header bar when sidebar is closed */}
        {!sidebarOpen && (
          <div className="sticky top-0 z-30 bg-dark-800 border-b border-dark-500 px-4 py-3 flex items-center gap-3">
            <button
              onClick={toggleSidebar}
              className="text-dark-200 hover:text-dark-50 p-1"
              aria-label="Open menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <Link to="/" className="flex items-center gap-2">
              <img src="/vite.svg" alt="Textborne logo" className="h-5 w-5" />
              <span className="text-lg font-bold text-primary-400">Textborne</span>
            </Link>
          </div>
        )}
        <div className="flex-1 p-8 max-w-5xl mx-auto w-full">
          <AccountContent user={user} />
        </div>
      </main>

      <CreateWorldWizard
        isOpen={showCreateWizard}
        onClose={() => { setShowCreateWizard(false); setImportHandoff(null); }}
        initialData={importHandoff}
      />

      <ImportWorldWizard
        isOpen={showImportWizard}
        onClose={() => setShowImportWizard(false)}
        onHandoffToCreate={(data) => {
          setImportHandoff(data);
          setShowImportWizard(false);
          setShowCreateWizard(true);
        }}
      />

      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </div>
  );
}

export function AccountModal() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  if (!user) return null;

  const handleClose = () => {
    if (location.state && (location.state as { backgroundLocation?: Location }).backgroundLocation) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-dark-900 border border-dark-600 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6">
        <AccountContent user={user} variant="modal" onClose={handleClose} />
      </div>
    </div>
  );
}
